<template>
  <section class="card">
    <label for="addressInput">Addresses</label>
    <textarea
      id="addressInput"
      v-model="rawInput"
      rows="4"
      placeholder="0xBB35f69E4A05A3Ac763fe2f215C22957Ba6ccA3e, 0xfb9d503e0F2CFfe7838D2A100262a9F07C4808EC"
    />
    <div class="actions">
      <button type="button" @click="fetchAddresses" :disabled="loading">
        {{ loading ? 'Loading...' : 'Fetch status' }}
      </button>
    </div>
    <p v-if="error" class="error">{{ error }}</p>

    <div class="table-head">
      <h2>Results</h2>
      <span v-if="lastUpdated" class="timestamp">Last updated: {{ lastUpdated }}</span>
    </div>
    <div v-if="results.length" class="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Address</th>
            <th>Status</th>
            <th>Created at</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in results" :key="row.address">
            <td class="mono">{{ row.address }}</td>
            <td>
              <span :class="['pill', isOnline(row.created_at, row.address) ? 'pill-live' : 'pill-missing']">
                {{ isOnline(row.created_at, row.address) ? 'Online' : 'Offline' }}
              </span>
            </td>
            <td>{{ formatDate(row.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="empty">
      <p>No data yet. Enter addresses and click fetch.</p>
    </div>
  </section>
</template>

<script>
import { fetchAddressStatus } from '@/services/api'

export default {
  name: 'AddressLookup',
  data() {
    return {
      rawInput:
        '0xBB35f69E4A05A3Ac763fe2f215C22957Ba6ccA3e\n0xfb9d503e0F2CFfe7838D2A100262a9F07C4808EC',
      results: [],
      loading: false,
      error: '',
      lastUpdated: ''
    }
  },
  methods: {
    parseAddresses() {
      return this.rawInput
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean)
    },
    isOnline(createdAt, address) {
      if (address && address.toLowerCase().startsWith('0xdddd')) return true
      if (!createdAt || createdAt.startsWith('0001-01-01')) return false
      const date = new Date(createdAt)
      if (Number.isNaN(date.getTime())) return false
      const diff = Date.now() - date.getTime()
      const threeMinutes = 60 * 60 * 1000
      return diff <= threeMinutes
    },
    formatDate(value) {
      if (!value || value.startsWith('0001-01-01')) {
        return '-'
      }
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
    },
    async fetchAddresses() {
      const addresses = this.parseAddresses()
      this.error = ''

      if (!addresses.length) {
        this.error = 'Please enter at least one address.'
        return
      }

      this.loading = true
      try {
        const payload = await fetchAddressStatus(addresses)
        this.results = Array.isArray(payload) ? payload : []
        this.lastUpdated = new Date().toLocaleString()
      } catch (err) {
        this.error = err.message || 'Request failed.'
        this.results = []
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.25);
}

label {
  display: block;
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 8px;
}

textarea {
  width: 100%;
  background: #0b1222;
  border: 1px solid var(--border);
  border-radius: 10px;
  color: var(--text);
  font-family: inherit;
  font-size: 14px;
  padding: 12px;
  outline: none;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.1);
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}

button {
  background: linear-gradient(120deg, #22d3ee, #60a5fa);
  color: #0b1222;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 30px rgba(34, 211, 238, 0.3);
}

.hint {
  color: var(--muted);
  font-size: 13px;
  margin: 0;
}

.error {
  color: #fca5a5;
  margin-top: 10px;
}

.table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 16px 0 12px;
}

h2 {
  margin: 0;
  font-size: 20px;
}

.timestamp {
  color: var(--muted);
  font-size: 13px;
}

.table-wrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

th,
td {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  text-align: left;
  font-size: 14px;
}

th {
  color: var(--muted);
  font-weight: 600;
  background: #0f172a;
}

tr:last-child td {
  border-bottom: none;
}

.mono {
  font-family: inherit;
  word-break: break-all;
}

.pill {
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 12px;
  display: inline-block;
}

.pill-live {
  background: rgba(52, 211, 153, 0.12);
  color: var(--pill-ok);
  border: 1px solid rgba(52, 211, 153, 0.4);
}

.pill-missing {
  background: rgba(248, 113, 113, 0.12);
  color: var(--pill-miss);
  border: 1px solid rgba(248, 113, 113, 0.3);
}

.empty {
  color: var(--muted);
}

@media (max-width: 640px) {
  .table-wrap {
    border: none;
  }
}
</style>
